import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Plus,
  User,
  Users,
  FileText,
  Image,
  Loader2,
  Calendar,
  MapPin,
  Briefcase,
  Pencil,
  Link as LinkIcon,
  UserPlus,
  Mail,
  Clock,
  Crown,
  X,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import type { Project, PersonWithCounts, Gender, CreatePersonInput, RelationType } from '@antree/shared';
import { FamilyTree } from '@/components/tree/FamilyTree';

interface Relation {
  id: string;
  projectId: string;
  subjectId: string;
  relativeId: string;
  type: RelationType;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;
  subject: { id: string; firstName: string | null; lastName: string | null };
  relative: { id: string; firstName: string | null; lastName: string | null };
}

interface RelationsResponse {
  relations: Relation[];
}

interface ProjectResponse {
  project: Project & { _count?: { persons: number; documents: number; photos: number } };
}

interface PersonsResponse {
  persons: PersonWithCounts[];
}

interface CreatePersonResponse {
  person: PersonWithCounts;
}

interface ProjectAccessUser {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: 'OWNER' | 'EDITOR' | 'VIEWER';
  createdAt: string;
}

interface ProjectInvitation {
  id: string;
  projectId: string;
  email: string;
  role: 'EDITOR' | 'VIEWER';
  invitedBy: string;
  createdAt: string;
}

interface AccessResponse {
  access: ProjectAccessUser[];
  invitations: ProjectInvitation[];
}

// Draggable & Droppable Person Card Component
function DraggablePersonCard({
  person,
  projectId,
  formatName,
  formatLifespan,
  getGenderIcon,
  isOver,
  isDragging,
}: {
  person: PersonWithCounts;
  projectId: string;
  formatName: (p: PersonWithCounts) => string;
  formatLifespan: (p: PersonWithCounts) => string;
  getGenderIcon: (gender: Gender | null) => React.ReactNode;
  isOver: boolean;
  isDragging: boolean;
}) {
  const navigate = useNavigate();

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging: isCurrentlyDragging,
  } = useDraggable({
    id: person.id,
    data: { person },
  });

  const { setNodeRef: setDropRef, isOver: isDropOver } = useDroppable({
    id: `drop-${person.id}`,
    data: { person },
  });

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    zIndex: isCurrentlyDragging ? 1000 : undefined,
  };

  return (
    <div
      ref={(node) => {
        setDragRef(node);
        setDropRef(node);
      }}
      style={style}
    >
      {/* Clickable card */}
      <Card
        className={`h-full transition-all cursor-pointer ${
          isDropOver || isOver ? 'border-primary border-2 bg-primary/5 scale-105' : 'hover:border-primary/50'
        } ${isDragging || isCurrentlyDragging ? 'opacity-50' : ''}`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              {getGenderIcon(person.gender)}
            </div>
            <div
              className="flex-1 min-w-0"
              onClick={() => navigate(`/projects/${projectId}/persons/${person.id}`)}
            >
              <CardTitle className="text-base truncate">{formatName(person)}</CardTitle>
              {person.birthName && person.birthName !== person.lastName && (
                <CardDescription className="text-xs">geb. {person.birthName}</CardDescription>
              )}
            </div>
            {/* Drag handle */}
            <div
              {...listeners}
              {...attributes}
              className="p-2 -mr-2 -mt-1 cursor-grab active:cursor-grabbing hover:bg-primary/10 rounded border border-dashed border-muted-foreground/30 touch-none"
              onClick={(e) => e.stopPropagation()}
              title="Ziehen um zu verknupfen"
            >
              <LinkIcon className="h-5 w-5 text-primary" />
            </div>
          </div>
        </CardHeader>
        <CardContent
          className="pt-0"
          onClick={() => navigate(`/projects/${projectId}/persons/${person.id}`)}
        >
          <div className="space-y-1 text-sm text-muted-foreground">
            {(person.birthDate || person.deathDate) && (
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatLifespan(person)}</span>
              </div>
            )}
            {person.occupation && (
              <div className="flex items-center gap-2">
                <Briefcase className="h-3.5 w-3.5" />
                <span className="truncate">{person.occupation}</span>
              </div>
            )}
            {person.birthPlace && (
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" />
                <span className="truncate">{person.birthPlace.name}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectResponse['project'] | null>(null);
  const [persons, setPersons] = useState<PersonWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Project edit state
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectDescription, setEditProjectDescription] = useState('');
  const [editProjectError, setEditProjectError] = useState<string | null>(null);

  // Person form state
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthName, setBirthName] = useState('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [birthDate, setBirthDate] = useState('');
  const [deathDate, setDeathDate] = useState('');
  const [occupation, setOccupation] = useState('');

  // Drag & Drop state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Link persons dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkSource, setLinkSource] = useState<PersonWithCounts | null>(null);
  const [linkTarget, setLinkTarget] = useState<PersonWithCounts | null>(null);
  const [linkType, setLinkType] = useState<RelationType | ''>('');
  const [linkingPersons, setLinkingPersons] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [existingRelation, setExistingRelation] = useState<Relation | null>(null);
  const [checkingRelation, setCheckingRelation] = useState(false);
  const [deletingRelation, setDeletingRelation] = useState(false);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareRole, setShareRole] = useState<'EDITOR' | 'VIEWER'>('VIEWER');
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState<string | null>(null);
  const [accessList, setAccessList] = useState<ProjectAccessUser[]>([]);
  const [invitationsList, setInvitationsList] = useState<ProjectInvitation[]>([]);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [cancelingInvitationId, setCancelingInvitationId] = useState<string | null>(null);

  // DnD sensors with activation constraint to distinguish from clicks
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10, // 10px movement before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // 250ms hold before drag starts on touch
        tolerance: 5,
      },
    })
  );

  useEffect(() => {
    if (id) {
      loadProject();
      loadPersons();
    }
  }, [id]);

  async function loadProject() {
    const response = await api.get<ProjectResponse>(`/projects/${id}`);
    if (response.success && response.data) {
      setProject(response.data.project);
    }
  }

  async function loadPersons() {
    setLoading(true);
    const response = await api.get<PersonsResponse>(`/projects/${id}/persons`);
    if (response.success && response.data) {
      setPersons(response.data.persons);
    }
    setLoading(false);
  }

  function resetForm() {
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setBirthName('');
    setGender('');
    setBirthDate('');
    setDeathDate('');
    setOccupation('');
    setError(null);
  }

  function handleDragStart(event: DragStartEvent) {
    console.log('Drag started:', event.active.id);
    setActiveId(event.active.id as string);
  }

  function handleDragOver(event: DragEndEvent) {
    const overId = event.over?.id as string | undefined;
    if (overId?.startsWith('drop-')) {
      setOverId(overId.replace('drop-', ''));
    } else {
      setOverId(null);
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const sourcePersonId = active.id as string;
    const targetId = over.id as string;

    // Extract target person ID from drop zone ID
    const targetPersonId = targetId.startsWith('drop-') ? targetId.replace('drop-', '') : null;

    if (!targetPersonId || sourcePersonId === targetPersonId) return;

    // Find the persons
    const sourcePerson = persons.find((p) => p.id === sourcePersonId);
    const targetPerson = persons.find((p) => p.id === targetPersonId);

    if (!sourcePerson || !targetPerson) return;

    // Set source and target
    setLinkSource(sourcePerson);
    setLinkTarget(targetPerson);
    setLinkType('');
    setLinkError(null);
    setExistingRelation(null);
    setCheckingRelation(true);
    setLinkDialogOpen(true);

    // Check for existing relation
    const response = await api.get<RelationsResponse>(`/projects/${id}/relations`);
    if (response.success && response.data) {
      // Find relation between these two persons (in either direction)
      const found = response.data.relations.find(
        (r) =>
          (r.subjectId === sourcePersonId && r.relativeId === targetPersonId) ||
          (r.subjectId === targetPersonId && r.relativeId === sourcePersonId)
      );
      if (found) {
        setExistingRelation(found);
        setLinkType(found.type);
      }
    }
    setCheckingRelation(false);
  }

  function handleDragCancel() {
    setActiveId(null);
    setOverId(null);
  }

  async function handleLinkPersons(e: React.FormEvent) {
    e.preventDefault();
    if (!linkSource || !linkTarget || !linkType) {
      setLinkError('Bitte Beziehungstyp auswählen');
      return;
    }

    setLinkingPersons(true);
    setLinkError(null);

    const response = await api.post(`/projects/${id}/relations`, {
      subjectId: linkSource.id,
      relativeId: linkTarget.id,
      type: linkType,
    });

    if (response.success) {
      setLinkDialogOpen(false);
      setLinkSource(null);
      setLinkTarget(null);
      setLinkType('');
      setExistingRelation(null);
      loadPersons();
    } else {
      setLinkError(response.error?.message || 'Fehler beim Verknüpfen');
    }
    setLinkingPersons(false);
  }

  async function handleDeleteRelation() {
    if (!existingRelation) return;

    setDeletingRelation(true);
    setLinkError(null);

    const response = await api.delete(`/projects/${id}/relations/${existingRelation.id}`);

    if (response.success) {
      setLinkDialogOpen(false);
      setLinkSource(null);
      setLinkTarget(null);
      setLinkType('');
      setExistingRelation(null);
      loadPersons();
    } else {
      setLinkError(response.error?.message || 'Fehler beim Löschen');
    }
    setDeletingRelation(false);
  }

  function getRelationLabel(type: RelationType): string {
    switch (type) {
      case 'PARENT': return 'Elternteil';
      case 'CHILD': return 'Kind';
      case 'SPOUSE': return 'Ehepartner';
      case 'PARTNER': return 'Partner';
      case 'SIBLING': return 'Geschwister';
      default: return type;
    }
  }

  function openEditProject() {
    if (project) {
      setEditProjectName(project.name);
      setEditProjectDescription(project.description || '');
      setEditProjectError(null);
      setEditProjectOpen(true);
    }
  }

  async function loadAccess() {
    setLoadingAccess(true);
    const response = await api.get<AccessResponse>(`/projects/${id}/access`);
    if (response.success && response.data) {
      setAccessList(response.data.access);
      setInvitationsList(response.data.invitations);
    }
    setLoadingAccess(false);
  }

  function openShareDialog() {
    setShareEmail('');
    setShareRole('VIEWER');
    setShareError(null);
    setShareSuccess(null);
    setShareDialogOpen(true);
    loadAccess();
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!shareEmail.trim()) {
      setShareError('Bitte E-Mail-Adresse eingeben');
      return;
    }

    setSharing(true);
    setShareError(null);
    setShareSuccess(null);

    const response = await api.post(`/projects/${id}/invite`, {
      email: shareEmail.trim().toLowerCase(),
      role: shareRole,
    });

    if (response.success) {
      setShareEmail('');
      setShareSuccess('Einladung wurde gesendet');
      loadAccess();
    } else {
      setShareError(response.error?.message || 'Fehler beim Einladen');
    }
    setSharing(false);
  }

  async function handleRemoveAccess(userId: string) {
    setRemovingUserId(userId);
    const response = await api.delete(`/projects/${id}/access/${userId}`);
    if (response.success) {
      loadAccess();
    }
    setRemovingUserId(null);
  }

  async function handleCancelInvitation(invitationId: string) {
    setCancelingInvitationId(invitationId);
    const response = await api.delete(`/projects/${id}/invitations/${invitationId}`);
    if (response.success) {
      loadAccess();
    }
    setCancelingInvitationId(null);
  }

  function getRoleLabel(role: string): string {
    switch (role) {
      case 'OWNER': return 'Besitzer';
      case 'EDITOR': return 'Bearbeiter';
      case 'VIEWER': return 'Betrachter';
      default: return role;
    }
  }

  async function handleUpdateProject(e: React.FormEvent) {
    e.preventDefault();
    if (!editProjectName.trim()) {
      setEditProjectError('Projektname ist erforderlich');
      return;
    }

    setEditingProject(true);
    setEditProjectError(null);

    const response = await api.put<ProjectResponse>(`/projects/${id}`, {
      name: editProjectName.trim(),
      description: editProjectDescription.trim() || null,
    });

    if (response.success && response.data) {
      setProject({
        ...project!,
        name: response.data.project.name,
        description: response.data.project.description,
      });
      setEditProjectOpen(false);
    } else {
      setEditProjectError(response.error?.message || 'Fehler beim Speichern');
    }
    setEditingProject(false);
  }

  async function handleCreatePerson(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName && !lastName) {
      setError('Bitte mindestens Vor- oder Nachname eingeben');
      return;
    }

    setCreating(true);
    setError(null);

    const data: CreatePersonInput = {};
    if (firstName) data.firstName = firstName;
    if (middleName) data.middleName = middleName;
    if (lastName) data.lastName = lastName;
    if (birthName) data.birthName = birthName;
    if (gender) data.gender = gender;
    if (birthDate) data.birthDate = birthDate;
    if (deathDate) data.deathDate = deathDate;
    if (occupation) data.occupation = occupation;

    const response = await api.post<CreatePersonResponse>(`/projects/${id}/persons`, data);

    if (response.success && response.data) {
      setPersons([response.data.person, ...persons]);
      setDialogOpen(false);
      resetForm();
      // Update project count
      if (project) {
        setProject({
          ...project,
          _count: {
            ...project._count,
            persons: (project._count?.persons || 0) + 1,
            documents: project._count?.documents || 0,
            photos: project._count?.photos || 0,
          },
        });
      }
    } else {
      setError(response.error?.message || 'Fehler beim Erstellen der Person');
    }
    setCreating(false);
  }

  function formatName(person: PersonWithCounts): string {
    const parts = [];
    if (person.firstName) parts.push(person.firstName);
    if (person.middleName) parts.push(person.middleName);
    if (person.lastName) parts.push(person.lastName);
    if (parts.length === 0) return 'Unbekannt';
    return parts.join(' ');
  }

  // Convert YYYY-MM-DD to DD.MM.YYYY
  function formatDateDE(dateStr: string | null): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  }

  function formatLifespan(person: PersonWithCounts): string {
    const parts = [];
    if (person.birthDate) parts.push(formatDateDE(person.birthDate));
    else parts.push('?');
    parts.push('–');
    if (person.deathDate) parts.push(formatDateDE(person.deathDate));
    else if (person.birthDate) parts.push('');
    if (parts.length <= 1) return '';
    return parts.join(' ').trim();
  }

  function getGenderIcon(gender: Gender | null) {
    switch (gender) {
      case 'MALE':
        return <User className="h-4 w-4 text-blue-500" />;
      case 'FEMALE':
        return <User className="h-4 w-4 text-pink-500" />;
      default:
        return <User className="h-4 w-4 text-muted-foreground" />;
    }
  }

  if (!project && !loading) {
    return (
      <div className="container py-8">
        <p>Projekt nicht gefunden.</p>
        <Link to="/dashboard">
          <Button variant="link" className="px-0 hover:text-bronze-500">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zum Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="flex flex-col gap-6">
        {/* Back link */}
        <Link to="/dashboard">
          <Button variant="ghost" className="px-0 hover:bg-transparent hover:text-bronze-500">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zum Dashboard
          </Button>
        </Link>

        {/* Project Header */}
        {project && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{project.name}</h1>
                {project.description && (
                  <p className="text-muted-foreground mt-1">{project.description}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                onClick={openEditProject}
                title="Projekt bearbeiten"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                onClick={openShareDialog}
                title="Projekt teilen"
              >
                <UserPlus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>{project._count?.persons || 0} Personen</span>
              </div>
              <Link
                to={`/projects/${id}/documents`}
                className="flex items-center gap-1 hover:text-bronze-500 transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>{project._count?.documents || 0} Dokumente</span>
              </Link>
              <div className="flex items-center gap-1">
                <Image className="h-4 w-4" />
                <span>{project._count?.photos || 0} Fotos</span>
              </div>
            </div>
          </div>
        )}

        {/* Edit Project Dialog */}
        <Dialog open={editProjectOpen} onOpenChange={setEditProjectOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Projekt bearbeiten</DialogTitle>
              <DialogDescription>
                Andere den Namen oder die Beschreibung deines Projekts.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateProject} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="editProjectName">Projektname</Label>
                <Input
                  id="editProjectName"
                  value={editProjectName}
                  onChange={(e) => setEditProjectName(e.target.value)}
                  placeholder="z.B. Familie Muller"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editProjectDescription">Beschreibung (optional)</Label>
                <Textarea
                  id="editProjectDescription"
                  value={editProjectDescription}
                  onChange={(e) => setEditProjectDescription(e.target.value)}
                  placeholder="Kurze Beschreibung des Projekts..."
                  rows={3}
                />
              </div>
              {editProjectError && <p className="text-sm text-red-500">{editProjectError}</p>}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditProjectOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={editingProject}>
                  {editingProject && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Share Project Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Projekt teilen</DialogTitle>
              <DialogDescription>
                Lade andere Nutzer ein, an diesem Projekt mitzuarbeiten.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Invite form */}
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="E-Mail-Adresse"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                    />
                  </div>
                  <Select value={shareRole} onValueChange={(v) => setShareRole(v as 'EDITOR' | 'VIEWER')}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EDITOR">Bearbeiter</SelectItem>
                      <SelectItem value="VIEWER">Betrachter</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="submit" disabled={sharing}>
                    {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  </Button>
                </div>
                {shareError && <p className="text-sm text-red-500">{shareError}</p>}
                {shareSuccess && <p className="text-sm text-green-600">{shareSuccess}</p>}
              </form>

              {/* Access list */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Zugriff</h4>
                {loadingAccess ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {accessList.map((access) => (
                      <div
                        key={access.id}
                        className="flex items-center justify-between p-2 rounded-lg border bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                            {access.role === 'OWNER' ? (
                              <Crown className="h-4 w-4 text-primary" />
                            ) : (
                              <User className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{access.userName}</p>
                            <p className="text-xs text-muted-foreground">{access.userEmail}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{getRoleLabel(access.role)}</span>
                          {access.role !== 'OWNER' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-red-500"
                              onClick={() => handleRemoveAccess(access.userId)}
                              disabled={removingUserId === access.userId}
                            >
                              {removingUserId === access.userId ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <X className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Pending invitations */}
                    {invitationsList.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium mt-4 pt-2 border-t">Ausstehende Einladungen</h4>
                        {invitationsList.map((invitation) => (
                          <div
                            key={invitation.id}
                            className="flex items-center justify-between p-2 rounded-lg border border-dashed bg-muted/10"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div>
                                <p className="text-sm">{invitation.email}</p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Eingeladen
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{getRoleLabel(invitation.role)}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-red-500"
                                onClick={() => handleCancelInvitation(invitation.id)}
                                disabled={cancelingInvitationId === invitation.id}
                              >
                                {cancelingInvitationId === invitation.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <X className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Actions */}
        <div className="flex gap-2">
          <Link to={`/projects/${id}/documents`}>
            <Button variant="outline">
              <FileText className="mr-2 h-4 w-4" />
              Dokumente
            </Button>
          </Link>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Person hinzufugen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Neue Person</DialogTitle>
                <DialogDescription>
                  Erfasse die Grunddaten einer neuen Person in deinem Stammbaum.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePerson} className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Vorname</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Max"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="middleName">Zweiter Vorname</Label>
                    <Input
                      id="middleName"
                      value={middleName}
                      onChange={(e) => setMiddleName(e.target.value)}
                      placeholder="Heinrich"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nachname</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Mustermann"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="birthName">Geburtsname</Label>
                    <Input
                      id="birthName"
                      value={birthName}
                      onChange={(e) => setBirthName(e.target.value)}
                      placeholder="Falls abweichend"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Geschlecht</Label>
                    <Select value={gender} onValueChange={(v) => setGender(v as Gender)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MALE">Männlich</SelectItem>
                        <SelectItem value="FEMALE">Weiblich</SelectItem>
                        <SelectItem value="OTHER">Divers</SelectItem>
                        <SelectItem value="UNKNOWN">Unbekannt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="birthDate">Geburtsdatum</Label>
                    <Input
                      id="birthDate"
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deathDate">Sterbedatum</Label>
                    <Input
                      id="deathDate"
                      type="date"
                      value={deathDate}
                      onChange={(e) => setDeathDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="occupation">Beruf</Label>
                  <Input
                    id="occupation"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    placeholder="z.B. Schreiner"
                  />
                </div>

                {error && <p className="text-sm text-red-500">{error}</p>}

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Erstellen
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Family Tree */}
        {persons.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Stammbaum</CardTitle>
              <CardDescription>
                Klicke auf eine Person, um Details anzuzeigen. Nutze Maus oder Touch zum Zoomen und Verschieben.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FamilyTree projectId={id!} />
            </CardContent>
          </Card>
        )}

        {/* Persons List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Personen</h2>
            {persons.length > 1 && (
              <p className="text-sm text-muted-foreground">
                <LinkIcon className="h-3.5 w-3.5 inline mr-1" />
                Ziehe eine Person auf eine andere, um sie zu verknupfen
              </p>
            )}
          </div>
          {loading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : persons.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium">Noch keine Personen</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Fuge die erste Person zu deinem Stammbaum hinzu.
                </p>
              </CardContent>
            </Card>
          ) : (
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {persons.map((person) => (
                  <DraggablePersonCard
                    key={person.id}
                    person={person}
                    projectId={id!}
                    formatName={formatName}
                    formatLifespan={formatLifespan}
                    getGenderIcon={getGenderIcon}
                    isOver={overId === person.id}
                    isDragging={activeId === person.id}
                  />
                ))}
              </div>
              <DragOverlay>
                {activeId ? (
                  <Card className="h-full border-primary shadow-lg">
                    <CardHeader className="pb-2">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                          {getGenderIcon(persons.find((p) => p.id === activeId)?.gender || null)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base truncate">
                            {formatName(persons.find((p) => p.id === activeId)!)}
                          </CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>

        {/* Link Persons Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {checkingRelation
                  ? 'Prüfe Verknüpfung...'
                  : existingRelation
                  ? 'Bestehende Verknüpfung'
                  : 'Personen verknüpfen'}
              </DialogTitle>
              <DialogDescription>
                {checkingRelation ? (
                  'Prüfe ob eine Verknüpfung besteht...'
                ) : existingRelation ? (
                  <>
                    <strong>{linkSource ? formatName(linkSource) : ''}</strong> und{' '}
                    <strong>{linkTarget ? formatName(linkTarget) : ''}</strong> sind bereits verknüpft.
                  </>
                ) : (
                  <>
                    Wähle die Beziehung zwischen{' '}
                    <strong>{linkSource ? formatName(linkSource) : ''}</strong> und{' '}
                    <strong>{linkTarget ? formatName(linkTarget) : ''}</strong>.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            {checkingRelation ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : existingRelation ? (
              <div className="space-y-4 mt-4">
                {/* Show existing relation */}
                <div className="p-4 border rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-1">Bestehende Beziehung:</p>
                  <p className="font-medium">
                    {formatName(linkSource!)} ist <strong>{getRelationLabel(existingRelation.type)}</strong> von {formatName(linkTarget!)}
                  </p>
                </div>

                {linkError && <p className="text-sm text-red-500">{linkError}</p>}

                <div className="flex justify-between gap-2 pt-4">
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteRelation}
                    disabled={deletingRelation}
                  >
                    {deletingRelation && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Verknüpfung löschen
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>
                    Schließen
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleLinkPersons} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="linkType">Beziehungstyp</Label>
                  <Select value={linkType} onValueChange={(v) => setLinkType(v as RelationType)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Beziehung auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PARENT">
                        {linkSource ? formatName(linkSource) : ''} ist Elternteil von{' '}
                        {linkTarget ? formatName(linkTarget) : ''}
                      </SelectItem>
                      <SelectItem value="CHILD">
                        {linkSource ? formatName(linkSource) : ''} ist Kind von{' '}
                        {linkTarget ? formatName(linkTarget) : ''}
                      </SelectItem>
                      <SelectItem value="SPOUSE">
                        {linkSource ? formatName(linkSource) : ''} ist Ehepartner von{' '}
                        {linkTarget ? formatName(linkTarget) : ''}
                      </SelectItem>
                      <SelectItem value="PARTNER">
                        {linkSource ? formatName(linkSource) : ''} ist Partner von{' '}
                        {linkTarget ? formatName(linkTarget) : ''}
                      </SelectItem>
                      <SelectItem value="SIBLING">
                        {linkSource ? formatName(linkSource) : ''} ist Geschwister von{' '}
                        {linkTarget ? formatName(linkTarget) : ''}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {linkError && <p className="text-sm text-red-500">{linkError}</p>}
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={linkingPersons || !linkType}>
                    {linkingPersons && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Verknüpfen
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
